// apps/admin-ui/src/components/DashboardLayout.tsx
// Shared dashboard layout with header, status bar, and grid
// ALL RIGHTS RESERVED

import type { ReactNode } from 'react';
import type { Role } from '../utils/rbac';
import type { RoleDashboardLayout } from '../config/dashboardWidgets';
import { ROLE_DESCRIPTIONS } from '../utils/rbac';
import '../styles/dashboard-layout.css';

// =============================================================================
// TYPES
// =============================================================================

export interface DashboardLayoutProps {
  layout: RoleDashboardLayout;
  role: Role;
  employee: {
    name: string;
    roles: Role[];
  };
  systemStatus?: {
    status: 'operational' | 'degraded' | 'outage';
    uptime: number;
    queueDepth: number;
  };
  lastRefresh: Date | null;
  isLoading: boolean;
  onRefreshAll: () => void;
  children: ReactNode;
  executiveOverview?: ReactNode;
}

// =============================================================================
// HELPERS
// =============================================================================

function formatTime(date: Date | null): string {
  if (!date) return 'Never';
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function getStatusIndicator(status: 'operational' | 'degraded' | 'outage'): { icon: string; label: string; className: string } {
  switch (status) {
    case 'operational':
      return { icon: '●', label: 'Operational', className: 'status-operational' };
    case 'degraded':
      return { icon: '◐', label: 'Degraded', className: 'status-degraded' };
    case 'outage':
      return { icon: '○', label: 'Outage', className: 'status-outage' };
  }
}

// =============================================================================
// COMPONENT
// =============================================================================

export function DashboardLayout({
  layout,
  role,
  employee,
  systemStatus,
  lastRefresh,
  isLoading,
  onRefreshAll,
  children,
  executiveOverview,
}: DashboardLayoutProps) {
  const roleDescription = ROLE_DESCRIPTIONS[role];
  const statusIndicator = systemStatus ? getStatusIndicator(systemStatus.status) : null;

  return (
    <div className="dashboard-layout">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <h1 className="dashboard-title">{layout.title}</h1>
        </div>

        <div className="header-center">
          <div className="role-indicator">
            <span className="role-badge">{role}</span>
            {employee.roles.length > 1 && (
              <span className="role-count">+{employee.roles.length - 1}</span>
            )}
            <span className="role-description">{roleDescription}</span>
          </div>
        </div>

        <div className="header-right">
          <span className="last-update">
            Updated: {formatTime(lastRefresh)}
          </span>
          <button
            className="refresh-button"
            onClick={onRefreshAll}
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing...' : 'Refresh All'}
          </button>
        </div>
      </header>

      {/* System Status Bar */}
      {layout.showSystemStatus && systemStatus && statusIndicator && (
        <div className={`system-status-bar ${statusIndicator.className}`}>
          <span className="status-indicator">
            <span className="status-icon">{statusIndicator.icon}</span>
            <span className="status-label">{statusIndicator.label}</span>
          </span>
          <span className="status-metrics">
            <span className="metric">
              Uptime: <strong>{systemStatus.uptime.toFixed(2)}%</strong>
            </span>
            <span className="metric">
              Queue: <strong>{systemStatus.queueDepth}</strong>
            </span>
          </span>
        </div>
      )}

      {/* Executive Overview (for admin, council, treasury) */}
      {executiveOverview && (
        <section className="executive-overview-section">
          {executiveOverview}
        </section>
      )}

      {/* Widget Grid */}
      <main className="dashboard-grid">
        {children}
      </main>

      {/* Footer */}
      <footer className="dashboard-footer">
        <p className="footer-timestamp">
          Last updated: {formatTime(lastRefresh)}
        </p>
      </footer>
    </div>
  );
}

export default DashboardLayout;
